/**
 * Device Sync schema 1.3 content change timestamps.
 * Do not stamp on sync success — only on user-visible content mutations.
 */

import type { ItineraryDay, Trip } from '../types/client';

/** UTC ISO-8601 instant (e.g. 2026-08-03T22:15:00.000Z). */
export function nowContentTimestamp(date: Date = new Date()): string {
  return date.toISOString();
}

/** Parse optional ISO stamp; return null when missing/invalid. Never invent "now". */
export function parseContentTimestamp(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const ms = Date.parse(s);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString();
}

/**
 * Stamp trip contentUpdatedAt (and optionally day.updatedAt for days in the write).
 * Returns a shallow-copied trip; does not mutate the input.
 */
export function stampTripContent(
  trip: Trip,
  options: { dayDates?: string[]; stampAllDays?: boolean; at?: Date } = {}
): Trip {
  const at = nowContentTimestamp(options.at ?? new Date());
  const days = trip.itineraryDays;
  let nextDays = days;
  if (days?.length) {
    if (options.stampAllDays) {
      nextDays = days.map((d) => ({ ...d, updatedAt: at }));
    } else if (options.dayDates?.length) {
      const set = new Set(options.dayDates);
      nextDays = days.map((d) => (set.has(d.date) ? { ...d, updatedAt: at } : d));
    }
  }
  return {
    ...trip,
    contentUpdatedAt: at,
    itineraryDays: nextDays,
  };
}

/** Stamp updatedAt on a single day (for day-scoped edits). */
export function stampDayContent(day: ItineraryDay, at: Date = new Date()): ItineraryDay {
  return { ...day, updatedAt: nowContentTimestamp(at) };
}

/**
 * iOS needsContentSync: missing stamp or missing lastSuccessfulSync ⇒ dirty;
 * else contentUpdatedAt > lastSuccessfulSync.
 */
export function needsContentSync(
  contentUpdatedAt: string | null | undefined,
  lastSuccessfulSync: string | null | undefined
): boolean {
  if (contentUpdatedAt == null || !String(contentUpdatedAt).trim()) return true;
  if (lastSuccessfulSync == null || !String(lastSuccessfulSync).trim()) return true;
  const contentMs = Date.parse(contentUpdatedAt);
  const syncMs = Date.parse(lastSuccessfulSync);
  if (!Number.isFinite(contentMs) || !Number.isFinite(syncMs)) return true;
  return contentMs > syncMs;
}
